import { cloneCanonicalJson, hashCanonicalJson } from './canonical-json';
import { QuoteContractError, type JsonObject } from './contracts';

export const QUOTE_INTELLIGENCE_MODEL_ROLES_V1 = Object.freeze([
    'structured_extraction',
    'multimodal_evidence_diagnosis',
    'pipeline_author',
    'quote_author',
    'independent_critic',
    'embeddings',
    'audio_transcription',
] as const);

export type QuoteIntelligenceModelRoleV1 = typeof QUOTE_INTELLIGENCE_MODEL_ROLES_V1[number];

export const QUOTE_ENGINE_GOVERNED_STAGE_KEYS_V1 = Object.freeze([
    'diagnosis',
    'strategy',
    'wms',
    'scope-morph',
    'quote-generation',
] as const);

export type QuoteEngineGovernedStageKeyV1 = typeof QUOTE_ENGINE_GOVERNED_STAGE_KEYS_V1[number];

export const QUOTE_ENGINE_STAGE_MODEL_ROLE_V1: Readonly<Record<QuoteEngineGovernedStageKeyV1, QuoteIntelligenceModelRoleV1>> = Object.freeze({
    diagnosis: 'multimodal_evidence_diagnosis',
    strategy: 'pipeline_author',
    wms: 'pipeline_author',
    'scope-morph': 'pipeline_author',
    'quote-generation': 'quote_author',
});

const REASONING_EFFORTS = new Set(['max', 'xhigh', 'high', 'medium', 'low', 'minimal', 'none']);
const ASSIGNMENT_KEYS = new Set([
    'role',
    'candidateId',
    'modelId',
    'runtimeProfile',
    'provider',
    'singleAttempt',
    'allowProviderFallback',
    'allowModelSubstitution',
]);
const RUNTIME_PROFILE_KEYS = new Set(['reasoningEffort']);

export interface PortableModelRoutingAssignmentV1 {
    role: QuoteIntelligenceModelRoleV1;
    candidateId: string;
    modelId: string;
    runtimeProfile: { reasoningEffort?: string };
    provider: 'openrouter';
    singleAttempt: true;
    allowProviderFallback: false;
    allowModelSubstitution: false;
}

export interface PortableModelRoutingRevisionV1 {
    schemaVersion: 'quote-intelligence-model-routing-revision-v1';
    revisionId: string;
    createdAt: string;
    activatedBy: string;
    catalogueSnapshotHashes: string[];
    assignments: Record<QuoteIntelligenceModelRoleV1, PortableModelRoutingAssignmentV1>;
    revisionHash: string;
}

export interface PortableModelRoutingRunBindingV1 {
    schemaVersion: 'quote-intelligence-model-routing-run-binding-v1';
    runId: string;
    boundAt: string;
    routingRevisionId: string;
    routingRevisionHash: string;
}

export interface PortableModelRoutingConfigurationV1 {
    schemaVersion: 'quote-intelligence-model-routing-configuration-v1';
    binding: PortableModelRoutingRunBindingV1;
    revision: PortableModelRoutingRevisionV1;
}

/**
 * Portable, run-bound descriptor sent through the existing browser gateway.
 * It contains the complete configuration snapshot so the backend can
 * independently recompute (rather than trust) configurationSnapshotHash.
 */
export interface PortableGovernedModelRouteV1 {
    schemaVersion: 'quote-engine-legacy-governed-model-route-v1';
    runId: string;
    stage: QuoteEngineGovernedStageKeyV1;
    role: QuoteIntelligenceModelRoleV1;
    candidateId: string;
    model: string;
    provider: 'openrouter';
    reasoningEffort: string | null;
    singleAttempt: true;
    allowProviderFallback: false;
    allowModelSubstitution: false;
    fallbackModels: readonly [];
    routingRevisionId: string;
    routingRevisionHash: string;
    configurationSnapshotHash: string;
    configurationSnapshot: JsonObject;
    routingReceiptRef: string;
}

export interface PortableGovernedRoutingReceiptV1 {
    schemaVersion: 'quote-engine-governed-routing-receipt-v1';
    runId: string;
    stage: QuoteEngineGovernedStageKeyV1;
    role: QuoteIntelligenceModelRoleV1;
    candidateId: string;
    model: string;
    provider: 'openrouter';
    reasoningEffort: string | null;
    singleAttempt: true;
    upstreamAttempt: 1;
    gatewayRequestId: string;
    tenantId: string;
    routingRevisionId: string;
    routingRevisionHash: string;
    configurationSnapshotHash: string;
    routingReceiptRef: string;
    receiptHash: string;
}

export const QUOTE_ENGINE_GOVERNED_SUPPORT_OPERATIONS_V1 = Object.freeze([
    'embeddings',
    'audio_transcription',
    'independent_quote_critic',
] as const);

export type QuoteEngineGovernedSupportOperationV1 = typeof QUOTE_ENGINE_GOVERNED_SUPPORT_OPERATIONS_V1[number];

export const QUOTE_ENGINE_SUPPORT_OPERATION_ROLE_V1: Readonly<Record<
    QuoteEngineGovernedSupportOperationV1,
    QuoteIntelligenceModelRoleV1
>> = Object.freeze({
    embeddings: 'embeddings',
    audio_transcription: 'audio_transcription',
    independent_quote_critic: 'independent_critic',
});

export interface PortableGovernedSupportCapabilityV1 {
    schemaVersion: 'quote-engine-governed-support-capability-v1';
    operation: QuoteEngineGovernedSupportOperationV1;
    gatewayEndpoint: '/api/ai/embeddings' | '/api/ai/chat/vision' | '/api/ai/chat';
    providerEndpoint: 'embeddings' | 'chat/completions';
    inputModalities: readonly ['text'] | readonly ['text', 'audio'];
    outputModalities: readonly ['embeddings'] | readonly ['text'];
    maxInputCount: number;
    embeddingDimensions: number | null;
    approvedAudioFormats: readonly string[];
    instruction: string | null;
    temperature: number | null;
    maxOutputTokens: number | null;
    acceptedFinishReasons: readonly string[];
}

/**
 * Immutable support-role descriptor derived from the exact run-bound routing
 * revision. `ownerStage` prevents a nested helper from replaying the support
 * route outside the stage that owns its AbortSignal.
 */
export interface PortableGovernedSupportRouteV1 {
    schemaVersion: 'quote-engine-governed-support-route-v1';
    runId: string;
    ownerStage: QuoteEngineGovernedStageKeyV1;
    operation: QuoteEngineGovernedSupportOperationV1;
    role: QuoteIntelligenceModelRoleV1;
    candidateId: string;
    model: string;
    provider: 'openrouter';
    reasoningEffort: string | null;
    singleKey: true;
    singleAttempt: true;
    allowProviderFallback: false;
    allowModelSubstitution: false;
    fallbackModels: readonly [];
    capability: PortableGovernedSupportCapabilityV1;
    capabilityHash: string;
    routingRevisionId: string;
    routingRevisionHash: string;
    configurationSnapshotHash: string;
    configurationSnapshot: JsonObject;
    supportReceiptRef: string;
}

export interface PortableGovernedSupportRoutingReceiptV1 {
    schemaVersion: 'quote-engine-governed-support-routing-receipt-v1';
    runId: string;
    ownerStage: QuoteEngineGovernedStageKeyV1;
    operation: QuoteEngineGovernedSupportOperationV1;
    role: QuoteIntelligenceModelRoleV1;
    candidateId: string;
    model: string;
    provider: 'openrouter';
    reasoningEffort: string | null;
    singleKey: true;
    singleAttempt: true;
    upstreamAttempt: 1;
    gatewayRequestId: string;
    tenantId: string;
    capabilityHash: string;
    routingRevisionId: string;
    routingRevisionHash: string;
    configurationSnapshotHash: string;
    supportReceiptRef: string;
    requestDigest: string;
    inputEvidenceDigest: string;
    responseEvidenceDigest: string;
    inputCount: number;
    outputCount: number;
    outputDimensions: number | null;
    audioFormat: string | null;
    receiptHash: string;
}

export const GOVERNED_EMBEDDING_DIMENSIONS_V1 = 4096;
export const GOVERNED_EMBEDDING_MAX_INPUTS_V1 = 64;
export const GOVERNED_AUDIO_TRANSCRIPTION_INSTRUCTION_V1 =
    'Transcribe this audio file verbatim. Identify different speakers if possible. Output ONLY the transcript text.';
export const GOVERNED_AUDIO_TRANSCRIPTION_TEMPERATURE_V1 = 0;
export const GOVERNED_AUDIO_TRANSCRIPTION_MAX_OUTPUT_TOKENS_V1 = 16_384;
export const GOVERNED_AUDIO_TRANSCRIPTION_FINISH_REASONS_V1 = Object.freeze(['stop'] as const);
export const GOVERNED_AUDIO_FORMATS_V1 = Object.freeze([
    'wav', 'mp3', 'm4a', 'aac', 'ogg', 'flac', 'webm',
] as const);
export const GOVERNED_INDEPENDENT_QUOTE_CRITIC_INSTRUCTION_V1 =
    'You are the independent issue critic for an Australian building contractor (RJ Bird Building).';
export const GOVERNED_INDEPENDENT_QUOTE_CRITIC_TEMPERATURE_V1 = 0.1;
export const GOVERNED_INDEPENDENT_QUOTE_CRITIC_MAX_OUTPUT_TOKENS_V1 = 32_768;
export const GOVERNED_INDEPENDENT_QUOTE_CRITIC_FINISH_REASONS_V1 = Object.freeze(['stop'] as const);

const GOVERNED_ROUTE_KEYS = new Set([
    'schemaVersion',
    'runId',
    'stage',
    'role',
    'candidateId',
    'model',
    'provider',
    'reasoningEffort',
    'singleAttempt',
    'allowProviderFallback',
    'allowModelSubstitution',
    'fallbackModels',
    'routingRevisionId',
    'routingRevisionHash',
    'configurationSnapshotHash',
    'configurationSnapshot',
    'routingReceiptRef',
]);
const GOVERNED_RECEIPT_KEYS = new Set([
    'schemaVersion',
    'runId',
    'stage',
    'role',
    'candidateId',
    'model',
    'provider',
    'reasoningEffort',
    'singleAttempt',
    'upstreamAttempt',
    'gatewayRequestId',
    'tenantId',
    'routingRevisionId',
    'routingRevisionHash',
    'configurationSnapshotHash',
    'routingReceiptRef',
    'receiptHash',
]);
const GOVERNED_SUPPORT_ROUTE_KEYS = new Set([
    'schemaVersion',
    'runId',
    'ownerStage',
    'operation',
    'role',
    'candidateId',
    'model',
    'provider',
    'reasoningEffort',
    'singleKey',
    'singleAttempt',
    'allowProviderFallback',
    'allowModelSubstitution',
    'fallbackModels',
    'capability',
    'capabilityHash',
    'routingRevisionId',
    'routingRevisionHash',
    'configurationSnapshotHash',
    'configurationSnapshot',
    'supportReceiptRef',
]);
const GOVERNED_SUPPORT_RECEIPT_KEYS = new Set([
    'schemaVersion',
    'runId',
    'ownerStage',
    'operation',
    'role',
    'candidateId',
    'model',
    'provider',
    'reasoningEffort',
    'singleKey',
    'singleAttempt',
    'upstreamAttempt',
    'gatewayRequestId',
    'tenantId',
    'capabilityHash',
    'routingRevisionId',
    'routingRevisionHash',
    'configurationSnapshotHash',
    'supportReceiptRef',
    'requestDigest',
    'inputEvidenceDigest',
    'responseEvidenceDigest',
    'inputCount',
    'outputCount',
    'outputDimensions',
    'audioFormat',
    'receiptHash',
]);
const GOVERNED_SUPPORT_CAPABILITY_KEYS = new Set([
    'schemaVersion',
    'operation',
    'gatewayEndpoint',
    'providerEndpoint',
    'inputModalities',
    'outputModalities',
    'maxInputCount',
    'embeddingDimensions',
    'approvedAudioFormats',
    'instruction',
    'temperature',
    'maxOutputTokens',
    'acceptedFinishReasons',
]);

function fail(path: string, detail: string): never {
    throw new QuoteContractError(path, detail);
}

function record(value: unknown, path: string): Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) fail(path, 'expected an object');
    return value as Record<string, unknown>;
}

function string(value: unknown, path: string): string {
    if (typeof value !== 'string' || !value.trim()) fail(path, 'expected a non-empty string');
    return value;
}

function digest(value: unknown, path: string): string {
    const candidate = string(value, path);
    if (!/^[a-f0-9]{64}$/.test(candidate)) fail(path, 'expected a lowercase SHA-256 digest');
    return candidate;
}

function timestamp(value: unknown, path: string): string {
    const candidate = string(value, path);
    if (!Number.isFinite(Date.parse(candidate))) fail(path, 'expected an ISO timestamp');
    return candidate;
}

function providerFamily(modelId: string): string {
    return modelId.split('/')[0] || modelId;
}

function deepFreeze<T>(value: T): T {
    if (value && typeof value === 'object' && !Object.isFrozen(value)) {
        Object.freeze(value);
        for (const nested of Object.values(value as Record<string, unknown>)) deepFreeze(nested);
    }
    return value;
}

export const QUOTE_ENGINE_SUPPORT_CAPABILITIES_V1: Readonly<Record<
    QuoteEngineGovernedSupportOperationV1,
    PortableGovernedSupportCapabilityV1
>> = deepFreeze({
    embeddings: {
        schemaVersion: 'quote-engine-governed-support-capability-v1',
        operation: 'embeddings',
        gatewayEndpoint: '/api/ai/embeddings',
        providerEndpoint: 'embeddings',
        inputModalities: ['text'],
        outputModalities: ['embeddings'],
        maxInputCount: GOVERNED_EMBEDDING_MAX_INPUTS_V1,
        embeddingDimensions: GOVERNED_EMBEDDING_DIMENSIONS_V1,
        approvedAudioFormats: [],
        instruction: null,
        temperature: null,
        maxOutputTokens: null,
        acceptedFinishReasons: [],
    },
    audio_transcription: {
        schemaVersion: 'quote-engine-governed-support-capability-v1',
        operation: 'audio_transcription',
        gatewayEndpoint: '/api/ai/chat/vision',
        providerEndpoint: 'chat/completions',
        inputModalities: ['text', 'audio'],
        outputModalities: ['text'],
        maxInputCount: 1,
        embeddingDimensions: null,
        approvedAudioFormats: GOVERNED_AUDIO_FORMATS_V1,
        instruction: GOVERNED_AUDIO_TRANSCRIPTION_INSTRUCTION_V1,
        temperature: GOVERNED_AUDIO_TRANSCRIPTION_TEMPERATURE_V1,
        maxOutputTokens: GOVERNED_AUDIO_TRANSCRIPTION_MAX_OUTPUT_TOKENS_V1,
        acceptedFinishReasons: GOVERNED_AUDIO_TRANSCRIPTION_FINISH_REASONS_V1,
    },
    independent_quote_critic: {
        schemaVersion: 'quote-engine-governed-support-capability-v1',
        operation: 'independent_quote_critic',
        gatewayEndpoint: '/api/ai/chat',
        providerEndpoint: 'chat/completions',
        inputModalities: ['text'],
        outputModalities: ['text'],
        maxInputCount: 1,
        embeddingDimensions: null,
        approvedAudioFormats: [],
        instruction: GOVERNED_INDEPENDENT_QUOTE_CRITIC_INSTRUCTION_V1,
        temperature: GOVERNED_INDEPENDENT_QUOTE_CRITIC_TEMPERATURE_V1,
        maxOutputTokens: GOVERNED_INDEPENDENT_QUOTE_CRITIC_MAX_OUTPUT_TOKENS_V1,
        acceptedFinishReasons: GOVERNED_INDEPENDENT_QUOTE_CRITIC_FINISH_REASONS_V1,
    },
});

/** Validate the exact backend-produced routing envelope before a run owns it. */
export function assertPortableModelRoutingConfigurationV1(
    value: unknown,
    expectedRunId?: string,
): asserts value is PortableModelRoutingConfigurationV1 {
    const envelope = record(value, '$modelRouting');
    if (envelope.schemaVersion !== 'quote-intelligence-model-routing-configuration-v1') {
        fail('$modelRouting.schemaVersion', 'unexpected schema version');
    }
    const binding = record(envelope.binding, '$modelRouting.binding');
    if (binding.schemaVersion !== 'quote-intelligence-model-routing-run-binding-v1') {
        fail('$modelRouting.binding.schemaVersion', 'unexpected schema version');
    }
    const runId = string(binding.runId, '$modelRouting.binding.runId');
    if (expectedRunId !== undefined && runId !== expectedRunId) {
        fail('$modelRouting.binding.runId', 'does not match the Quote Engine run id');
    }
    timestamp(binding.boundAt, '$modelRouting.binding.boundAt');
    const bindingRevisionId = string(binding.routingRevisionId, '$modelRouting.binding.routingRevisionId');
    const bindingRevisionHash = digest(binding.routingRevisionHash, '$modelRouting.binding.routingRevisionHash');

    const revision = record(envelope.revision, '$modelRouting.revision');
    if (revision.schemaVersion !== 'quote-intelligence-model-routing-revision-v1') {
        fail('$modelRouting.revision.schemaVersion', 'unexpected schema version');
    }
    const revisionId = string(revision.revisionId, '$modelRouting.revision.revisionId');
    timestamp(revision.createdAt, '$modelRouting.revision.createdAt');
    string(revision.activatedBy, '$modelRouting.revision.activatedBy');
    const revisionHash = digest(revision.revisionHash, '$modelRouting.revision.revisionHash');
    if (bindingRevisionId !== revisionId || bindingRevisionHash !== revisionHash) {
        fail('$modelRouting.binding', 'does not bind the embedded routing revision');
    }
    if (!Array.isArray(revision.catalogueSnapshotHashes) || revision.catalogueSnapshotHashes.length === 0) {
        fail('$modelRouting.revision.catalogueSnapshotHashes', 'expected at least one catalogue snapshot hash');
    }
    revision.catalogueSnapshotHashes.forEach((entry, index) => (
        digest(entry, `$modelRouting.revision.catalogueSnapshotHashes[${index}]`)
    ));

    const assignments = record(revision.assignments, '$modelRouting.revision.assignments');
    const unknownRoles = Object.keys(assignments)
        .filter(role => !QUOTE_INTELLIGENCE_MODEL_ROLES_V1.includes(role as QuoteIntelligenceModelRoleV1));
    if (unknownRoles.length > 0) {
        fail('$modelRouting.revision.assignments', `unexpected role ${unknownRoles[0]}`);
    }
    for (const role of QUOTE_INTELLIGENCE_MODEL_ROLES_V1) {
        const assignment = record(assignments[role], `$modelRouting.revision.assignments.${role}`);
        const unknownAssignmentKeys = Object.keys(assignment).filter(key => !ASSIGNMENT_KEYS.has(key));
        if (unknownAssignmentKeys.length > 0) {
            fail(
                `$modelRouting.revision.assignments.${role}.${unknownAssignmentKeys[0]}`,
                'unexpected assignment field; fallback and substitution routes are forbidden',
            );
        }
        if (assignment.role !== role) fail(`$modelRouting.revision.assignments.${role}.role`, 'role mismatch');
        digest(assignment.candidateId, `$modelRouting.revision.assignments.${role}.candidateId`);
        const modelId = string(assignment.modelId, `$modelRouting.revision.assignments.${role}.modelId`);
        if (!modelId.includes('/')) fail(`$modelRouting.revision.assignments.${role}.modelId`, 'expected provider/model id');
        if (assignment.provider !== 'openrouter'
            || assignment.singleAttempt !== true
            || assignment.allowProviderFallback !== false
            || assignment.allowModelSubstitution !== false) {
            fail(`$modelRouting.revision.assignments.${role}`, 'fallback-safe routing policy is required');
        }
        const profile = record(assignment.runtimeProfile, `$modelRouting.revision.assignments.${role}.runtimeProfile`);
        const unknownProfileKeys = Object.keys(profile).filter(key => !RUNTIME_PROFILE_KEYS.has(key));
        if (unknownProfileKeys.length > 0) {
            fail(
                `$modelRouting.revision.assignments.${role}.runtimeProfile.${unknownProfileKeys[0]}`,
                'unexpected runtime-profile field',
            );
        }
        if (profile.reasoningEffort !== undefined
            && (typeof profile.reasoningEffort !== 'string' || !REASONING_EFFORTS.has(profile.reasoningEffort))) {
            fail(`$modelRouting.revision.assignments.${role}.runtimeProfile.reasoningEffort`, 'unsupported effort');
        }
    }
    const author = record(assignments.quote_author, '$modelRouting.revision.assignments.quote_author');
    const critic = record(assignments.independent_critic, '$modelRouting.revision.assignments.independent_critic');
    if (providerFamily(String(author.modelId)) === providerFamily(String(critic.modelId))) {
        fail('$modelRouting.revision.assignments.independent_critic', 'must use a different provider family from quote author');
    }

    const revisionIdentity = {
        schemaVersion: revision.schemaVersion,
        revisionId: revision.revisionId,
        createdAt: revision.createdAt,
        activatedBy: revision.activatedBy,
        catalogueSnapshotHashes: revision.catalogueSnapshotHashes,
        assignments: revision.assignments,
    };
    const calculatedHash = hashCanonicalJson(revisionIdentity).slice('sha256:'.length);
    if (calculatedHash !== revisionHash) {
        fail('$modelRouting.revision.revisionHash', 'does not match the embedded revision');
    }
}

function exactKeys(value: Record<string, unknown>, allowed: ReadonlySet<string>, path: string): void {
    const unexpected = Object.keys(value).find(key => !allowed.has(key));
    if (unexpected) fail(`${path}.${unexpected}`, 'unexpected field');
    const missing = [...allowed].find(key => !Object.prototype.hasOwnProperty.call(value, key));
    if (missing) fail(`${path}.${missing}`, 'required field is missing');
}

function prefixedDigest(value: unknown, path: string): string {
    const candidate = string(value, path);
    if (!/^sha256:[a-f0-9]{64}$/.test(candidate)) {
        fail(path, 'expected a sha256-prefixed lowercase digest');
    }
    return candidate;
}

function governedReceiptIdentity(value: Omit<PortableGovernedRoutingReceiptV1, 'receiptHash'>) {
    return {
        schemaVersion: value.schemaVersion,
        runId: value.runId,
        stage: value.stage,
        role: value.role,
        candidateId: value.candidateId,
        model: value.model,
        provider: value.provider,
        reasoningEffort: value.reasoningEffort,
        singleAttempt: value.singleAttempt,
        upstreamAttempt: value.upstreamAttempt,
        gatewayRequestId: value.gatewayRequestId,
        tenantId: value.tenantId,
        routingRevisionId: value.routingRevisionId,
        routingRevisionHash: value.routingRevisionHash,
        configurationSnapshotHash: value.configurationSnapshotHash,
        routingReceiptRef: value.routingReceiptRef,
    };
}

function governedSupportReceiptIdentity(
    value: Omit<PortableGovernedSupportRoutingReceiptV1, 'receiptHash'>,
) {
    return {
        schemaVersion: value.schemaVersion,
        runId: value.runId,
        ownerStage: value.ownerStage,
        operation: value.operation,
        role: value.role,
        candidateId: value.candidateId,
        model: value.model,
        provider: value.provider,
        reasoningEffort: value.reasoningEffort,
        singleKey: value.singleKey,
        singleAttempt: value.singleAttempt,
        upstreamAttempt: value.upstreamAttempt,
        gatewayRequestId: value.gatewayRequestId,
        tenantId: value.tenantId,
        capabilityHash: value.capabilityHash,
        routingRevisionId: value.routingRevisionId,
        routingRevisionHash: value.routingRevisionHash,
        configurationSnapshotHash: value.configurationSnapshotHash,
        supportReceiptRef: value.supportReceiptRef,
        requestDigest: value.requestDigest,
        inputEvidenceDigest: value.inputEvidenceDigest,
        responseEvidenceDigest: value.responseEvidenceDigest,
        inputCount: value.inputCount,
        outputCount: value.outputCount,
        outputDimensions: value.outputDimensions,
        audioFormat: value.audioFormat,
    };
}

function assertPortableGovernedSupportCapabilityV1(
    value: unknown,
    operation: QuoteEngineGovernedSupportOperationV1,
): asserts value is PortableGovernedSupportCapabilityV1 {
    const capability = record(value, '$governedSupportRoute.capability');
    exactKeys(capability, GOVERNED_SUPPORT_CAPABILITY_KEYS, '$governedSupportRoute.capability');
    const expected = QUOTE_ENGINE_SUPPORT_CAPABILITIES_V1[operation];
    if (hashCanonicalJson(capability as JsonObject) !== hashCanonicalJson(expected as unknown as JsonObject)) {
        fail('$governedSupportRoute.capability', 'does not match the approved support capability');
    }
}

/** Validate the descriptor again at every browser gateway call. */
export function assertPortableGovernedModelRouteV1(
    value: unknown,
    expected?: { runId?: string; stage?: QuoteEngineGovernedStageKeyV1 },
): asserts value is PortableGovernedModelRouteV1 {
    const route = record(value, '$governedModelRoute');
    exactKeys(route, GOVERNED_ROUTE_KEYS, '$governedModelRoute');
    if (route.schemaVersion !== 'quote-engine-legacy-governed-model-route-v1') {
        fail('$governedModelRoute.schemaVersion', 'unexpected schema version');
    }
    const runId = string(route.runId, '$governedModelRoute.runId');
    if (expected?.runId !== undefined && runId !== expected.runId) {
        fail('$governedModelRoute.runId', 'does not match the expected run');
    }
    if (!QUOTE_ENGINE_GOVERNED_STAGE_KEYS_V1.includes(route.stage as QuoteEngineGovernedStageKeyV1)) {
        fail('$governedModelRoute.stage', 'unsupported Quote Engine stage');
    }
    const stage = route.stage as QuoteEngineGovernedStageKeyV1;
    if (expected?.stage !== undefined && stage !== expected.stage) {
        fail('$governedModelRoute.stage', 'does not match the expected stage');
    }
    const expectedRole = QUOTE_ENGINE_STAGE_MODEL_ROLE_V1[stage];
    if (route.role !== expectedRole) fail('$governedModelRoute.role', 'does not match the governed stage role');
    const candidateId = digest(route.candidateId, '$governedModelRoute.candidateId');
    const model = string(route.model, '$governedModelRoute.model');
    if (!model.includes('/')) fail('$governedModelRoute.model', 'expected provider/model id');
    if (route.provider !== 'openrouter'
        || route.singleAttempt !== true
        || route.allowProviderFallback !== false
        || route.allowModelSubstitution !== false
        || !Array.isArray(route.fallbackModels)
        || route.fallbackModels.length !== 0) {
        fail('$governedModelRoute', 'one exact OpenRouter route with no fallback is required');
    }
    if (route.reasoningEffort !== null
        && (typeof route.reasoningEffort !== 'string' || !REASONING_EFFORTS.has(route.reasoningEffort))) {
        fail('$governedModelRoute.reasoningEffort', 'unsupported effort');
    }
    const routingRevisionId = string(route.routingRevisionId, '$governedModelRoute.routingRevisionId');
    const routingRevisionHash = digest(route.routingRevisionHash, '$governedModelRoute.routingRevisionHash');
    const configurationSnapshotHash = prefixedDigest(
        route.configurationSnapshotHash,
        '$governedModelRoute.configurationSnapshotHash',
    );
    const configurationSnapshot = record(
        route.configurationSnapshot,
        '$governedModelRoute.configurationSnapshot',
    ) as JsonObject;
    if (hashCanonicalJson(configurationSnapshot) !== configurationSnapshotHash) {
        fail('$governedModelRoute.configurationSnapshotHash', 'does not match the embedded configuration snapshot');
    }
    assertPortableModelRoutingConfigurationV1(configurationSnapshot.modelRouting, runId);
    const envelope = configurationSnapshot.modelRouting as unknown as PortableModelRoutingConfigurationV1;
    if (envelope.revision.revisionId !== routingRevisionId
        || envelope.revision.revisionHash !== routingRevisionHash) {
        fail('$governedModelRoute.routingRevisionHash', 'does not match the embedded routing envelope');
    }
    const assignment = envelope.revision.assignments[expectedRole];
    const assignedEffort = assignment.runtimeProfile.reasoningEffort ?? null;
    if (assignment.candidateId !== candidateId
        || assignment.modelId !== model
        || assignment.provider !== route.provider
        || assignedEffort !== route.reasoningEffort) {
        fail('$governedModelRoute', 'does not match the exact run-bound role assignment');
    }
    const expectedRef = [
        'model-routing',
        routingRevisionHash,
        stage,
        expectedRole,
        candidateId,
    ].join(':');
    if (route.routingReceiptRef !== expectedRef) {
        fail('$governedModelRoute.routingReceiptRef', 'does not match the routed assignment identity');
    }
}

/** Validate the content-free proof returned by the backend fetch boundary. */
export function assertPortableGovernedRoutingReceiptV1(
    value: unknown,
    route: PortableGovernedModelRouteV1,
): asserts value is PortableGovernedRoutingReceiptV1 {
    assertPortableGovernedModelRouteV1(route);
    const receipt = record(value, '$governedRoutingReceipt');
    exactKeys(receipt, GOVERNED_RECEIPT_KEYS, '$governedRoutingReceipt');
    if (receipt.schemaVersion !== 'quote-engine-governed-routing-receipt-v1') {
        fail('$governedRoutingReceipt.schemaVersion', 'unexpected schema version');
    }
    const expectedFields: Array<keyof PortableGovernedRoutingReceiptV1> = [
        'runId', 'stage', 'role', 'candidateId', 'model', 'provider',
        'reasoningEffort', 'singleAttempt', 'routingRevisionId',
        'routingRevisionHash', 'configurationSnapshotHash', 'routingReceiptRef',
    ];
    for (const key of expectedFields) {
        if (receipt[key] !== route[key as keyof PortableGovernedModelRouteV1]) {
            fail(`$governedRoutingReceipt.${key}`, 'does not match the governed route');
        }
    }
    if (receipt.upstreamAttempt !== 1) {
        fail('$governedRoutingReceipt.upstreamAttempt', 'expected the only permitted upstream attempt');
    }
    string(receipt.gatewayRequestId, '$governedRoutingReceipt.gatewayRequestId');
    string(receipt.tenantId, '$governedRoutingReceipt.tenantId');
    const receiptHash = prefixedDigest(receipt.receiptHash, '$governedRoutingReceipt.receiptHash');
    const identity = governedReceiptIdentity(receipt as unknown as PortableGovernedRoutingReceiptV1);
    if (hashCanonicalJson(identity) !== receiptHash) {
        fail('$governedRoutingReceipt.receiptHash', 'does not match the routing receipt');
    }
}

/** Derive one immutable support-role descriptor from the active stage route. */
export function resolvePortableGovernedSupportRouteV1(
    ownerRoute: PortableGovernedModelRouteV1,
    operation: QuoteEngineGovernedSupportOperationV1,
): PortableGovernedSupportRouteV1 {
    assertPortableGovernedModelRouteV1(ownerRoute);
    if (!QUOTE_ENGINE_GOVERNED_SUPPORT_OPERATIONS_V1.includes(operation)) {
        fail('$governedSupportRoute.operation', 'unsupported governed support operation');
    }
    if (operation === 'independent_quote_critic' && ownerRoute.stage !== 'quote-generation') {
        fail('$governedSupportRoute.ownerStage', 'independent quote critic must be owned by quote-generation');
    }
    const envelope = ownerRoute.configurationSnapshot.modelRouting as unknown as PortableModelRoutingConfigurationV1;
    const role = QUOTE_ENGINE_SUPPORT_OPERATION_ROLE_V1[operation];
    const assignment = envelope.revision.assignments[role];
    const capability = cloneCanonicalJson(
        QUOTE_ENGINE_SUPPORT_CAPABILITIES_V1[operation] as unknown as JsonObject,
        '$governedSupportRoute.capability',
    ) as unknown as PortableGovernedSupportCapabilityV1;
    const supportReceiptRef = [
        'model-routing-support',
        envelope.revision.revisionHash,
        ownerRoute.stage,
        operation,
        role,
        assignment.candidateId,
    ].join(':');
    const route = deepFreeze({
        schemaVersion: 'quote-engine-governed-support-route-v1' as const,
        runId: ownerRoute.runId,
        ownerStage: ownerRoute.stage,
        operation,
        role,
        candidateId: assignment.candidateId,
        model: assignment.modelId,
        provider: assignment.provider,
        reasoningEffort: assignment.runtimeProfile.reasoningEffort ?? null,
        singleKey: true as const,
        singleAttempt: true as const,
        allowProviderFallback: false as const,
        allowModelSubstitution: false as const,
        fallbackModels: Object.freeze([]) as readonly [],
        capability,
        capabilityHash: hashCanonicalJson(capability as unknown as JsonObject),
        routingRevisionId: ownerRoute.routingRevisionId,
        routingRevisionHash: ownerRoute.routingRevisionHash,
        configurationSnapshotHash: ownerRoute.configurationSnapshotHash,
        configurationSnapshot: cloneCanonicalJson(
            ownerRoute.configurationSnapshot,
            '$governedSupportRoute.configurationSnapshot',
        ),
        supportReceiptRef,
    });
    assertPortableGovernedSupportRouteV1(route);
    return route;
}

/** Validate a support descriptor at every browser gateway call. */
export function assertPortableGovernedSupportRouteV1(
    value: unknown,
    expected?: {
        runId?: string;
        ownerStage?: QuoteEngineGovernedStageKeyV1;
        operation?: QuoteEngineGovernedSupportOperationV1;
    },
): asserts value is PortableGovernedSupportRouteV1 {
    const route = record(value, '$governedSupportRoute');
    exactKeys(route, GOVERNED_SUPPORT_ROUTE_KEYS, '$governedSupportRoute');
    if (route.schemaVersion !== 'quote-engine-governed-support-route-v1') {
        fail('$governedSupportRoute.schemaVersion', 'unexpected schema version');
    }
    const runId = string(route.runId, '$governedSupportRoute.runId');
    if (expected?.runId !== undefined && runId !== expected.runId) {
        fail('$governedSupportRoute.runId', 'does not match the expected run');
    }
    if (!QUOTE_ENGINE_GOVERNED_STAGE_KEYS_V1.includes(route.ownerStage as QuoteEngineGovernedStageKeyV1)) {
        fail('$governedSupportRoute.ownerStage', 'unsupported owning Quote Engine stage');
    }
    const ownerStage = route.ownerStage as QuoteEngineGovernedStageKeyV1;
    if (expected?.ownerStage !== undefined && ownerStage !== expected.ownerStage) {
        fail('$governedSupportRoute.ownerStage', 'does not match the owning stage');
    }
    if (!QUOTE_ENGINE_GOVERNED_SUPPORT_OPERATIONS_V1.includes(
        route.operation as QuoteEngineGovernedSupportOperationV1,
    )) {
        fail('$governedSupportRoute.operation', 'unsupported governed support operation');
    }
    const operation = route.operation as QuoteEngineGovernedSupportOperationV1;
    if (expected?.operation !== undefined && operation !== expected.operation) {
        fail('$governedSupportRoute.operation', 'does not match the gateway operation');
    }
    if (operation === 'independent_quote_critic' && ownerStage !== 'quote-generation') {
        fail('$governedSupportRoute.ownerStage', 'independent quote critic must be owned by quote-generation');
    }
    const role = QUOTE_ENGINE_SUPPORT_OPERATION_ROLE_V1[operation];
    if (route.role !== role) fail('$governedSupportRoute.role', 'does not match the support operation role');
    const candidateId = digest(route.candidateId, '$governedSupportRoute.candidateId');
    const model = string(route.model, '$governedSupportRoute.model');
    if (!model.includes('/')) fail('$governedSupportRoute.model', 'expected provider/model id');
    if (route.provider !== 'openrouter'
        || route.singleKey !== true
        || route.singleAttempt !== true
        || route.allowProviderFallback !== false
        || route.allowModelSubstitution !== false
        || !Array.isArray(route.fallbackModels)
        || route.fallbackModels.length !== 0) {
        fail('$governedSupportRoute', 'one exact OpenRouter key and attempt with no fallback is required');
    }
    if (route.reasoningEffort !== null
        && (typeof route.reasoningEffort !== 'string' || !REASONING_EFFORTS.has(route.reasoningEffort))) {
        fail('$governedSupportRoute.reasoningEffort', 'unsupported effort');
    }
    if (operation === 'embeddings' && route.reasoningEffort !== null) {
        fail('$governedSupportRoute.reasoningEffort', 'embeddings capability does not accept reasoning');
    }
    assertPortableGovernedSupportCapabilityV1(route.capability, operation);
    const capabilityHash = prefixedDigest(route.capabilityHash, '$governedSupportRoute.capabilityHash');
    if (hashCanonicalJson(route.capability as unknown as JsonObject) !== capabilityHash) {
        fail('$governedSupportRoute.capabilityHash', 'does not match the approved capability');
    }
    const routingRevisionId = string(route.routingRevisionId, '$governedSupportRoute.routingRevisionId');
    const routingRevisionHash = digest(route.routingRevisionHash, '$governedSupportRoute.routingRevisionHash');
    const configurationSnapshotHash = prefixedDigest(
        route.configurationSnapshotHash,
        '$governedSupportRoute.configurationSnapshotHash',
    );
    const configurationSnapshot = record(
        route.configurationSnapshot,
        '$governedSupportRoute.configurationSnapshot',
    ) as JsonObject;
    if (hashCanonicalJson(configurationSnapshot) !== configurationSnapshotHash) {
        fail('$governedSupportRoute.configurationSnapshotHash', 'does not match the embedded configuration snapshot');
    }
    assertPortableModelRoutingConfigurationV1(configurationSnapshot.modelRouting, runId);
    const envelope = configurationSnapshot.modelRouting as unknown as PortableModelRoutingConfigurationV1;
    if (envelope.revision.revisionId !== routingRevisionId
        || envelope.revision.revisionHash !== routingRevisionHash) {
        fail('$governedSupportRoute.routingRevisionHash', 'does not match the embedded routing envelope');
    }
    const assignment = envelope.revision.assignments[role];
    if (assignment.candidateId !== candidateId
        || assignment.modelId !== model
        || assignment.provider !== route.provider
        || (assignment.runtimeProfile.reasoningEffort ?? null) !== route.reasoningEffort) {
        fail('$governedSupportRoute', 'does not match the exact run-bound support assignment');
    }
    const expectedRef = [
        'model-routing-support',
        routingRevisionHash,
        ownerStage,
        operation,
        role,
        candidateId,
    ].join(':');
    if (route.supportReceiptRef !== expectedRef) {
        fail('$governedSupportRoute.supportReceiptRef', 'does not match the support assignment identity');
    }
}

/** Validate the content-free support proof returned by the backend. */
export function assertPortableGovernedSupportRoutingReceiptV1(
    value: unknown,
    route: PortableGovernedSupportRouteV1,
): asserts value is PortableGovernedSupportRoutingReceiptV1 {
    assertPortableGovernedSupportRouteV1(route);
    const receipt = record(value, '$governedSupportRoutingReceipt');
    exactKeys(receipt, GOVERNED_SUPPORT_RECEIPT_KEYS, '$governedSupportRoutingReceipt');
    if (receipt.schemaVersion !== 'quote-engine-governed-support-routing-receipt-v1') {
        fail('$governedSupportRoutingReceipt.schemaVersion', 'unexpected schema version');
    }
    const expectedFields: Array<keyof PortableGovernedSupportRoutingReceiptV1> = [
        'runId', 'ownerStage', 'operation', 'role', 'candidateId', 'model', 'provider',
        'reasoningEffort', 'singleKey', 'singleAttempt', 'capabilityHash',
        'routingRevisionId', 'routingRevisionHash', 'configurationSnapshotHash',
        'supportReceiptRef',
    ];
    for (const key of expectedFields) {
        if (receipt[key] !== route[key as keyof PortableGovernedSupportRouteV1]) {
            fail(`$governedSupportRoutingReceipt.${key}`, 'does not match the governed support route');
        }
    }
    if (receipt.upstreamAttempt !== 1) {
        fail('$governedSupportRoutingReceipt.upstreamAttempt', 'expected the only permitted upstream attempt');
    }
    string(receipt.gatewayRequestId, '$governedSupportRoutingReceipt.gatewayRequestId');
    string(receipt.tenantId, '$governedSupportRoutingReceipt.tenantId');
    prefixedDigest(receipt.requestDigest, '$governedSupportRoutingReceipt.requestDigest');
    prefixedDigest(receipt.inputEvidenceDigest, '$governedSupportRoutingReceipt.inputEvidenceDigest');
    prefixedDigest(receipt.responseEvidenceDigest, '$governedSupportRoutingReceipt.responseEvidenceDigest');
    const inputCount = Number(receipt.inputCount);
    const outputCount = Number(receipt.outputCount);
    const outputDimensions = receipt.outputDimensions === null
        ? null
        : Number(receipt.outputDimensions);
    if (!Number.isSafeInteger(inputCount) || inputCount < 1
        || !Number.isSafeInteger(outputCount) || outputCount < 1
        || (receipt.outputDimensions !== null
            && (!Number.isSafeInteger(outputDimensions) || Number(outputDimensions) < 1))) {
        fail('$governedSupportRoutingReceipt', 'cardinality evidence is invalid');
    }
    if (receipt.audioFormat !== null
        && (typeof receipt.audioFormat !== 'string' || !receipt.audioFormat.trim())) {
        fail('$governedSupportRoutingReceipt.audioFormat', 'audio format evidence is invalid');
    }
    if (route.operation === 'embeddings') {
        if (inputCount > route.capability.maxInputCount
            || outputCount !== inputCount
            || outputDimensions !== route.capability.embeddingDimensions
            || receipt.audioFormat !== null) {
            fail('$governedSupportRoutingReceipt', 'embedding evidence does not match the approved capability');
        }
    } else if (route.operation === 'audio_transcription') {
        if (inputCount !== 1
            || outputCount !== 1
            || outputDimensions !== null
            || !route.capability.approvedAudioFormats.includes(String(receipt.audioFormat))) {
            fail('$governedSupportRoutingReceipt', 'audio evidence does not match the approved capability');
        }
    } else if (inputCount !== 1
        || outputCount !== 1
        || outputDimensions !== null
        || receipt.audioFormat !== null) {
        fail('$governedSupportRoutingReceipt', 'critic evidence does not match the approved capability');
    }
    const receiptHash = prefixedDigest(
        receipt.receiptHash,
        '$governedSupportRoutingReceipt.receiptHash',
    );
    const identity = governedSupportReceiptIdentity(
        receipt as unknown as PortableGovernedSupportRoutingReceiptV1,
    );
    if (hashCanonicalJson(identity) !== receiptHash) {
        fail('$governedSupportRoutingReceipt.receiptHash', 'does not match the support routing receipt');
    }
}

/** Attach, validate and freeze one routing revision inside a run snapshot. */
export function attachPortableModelRoutingConfigurationV1(
    baseConfiguration: JsonObject,
    modelRouting: PortableModelRoutingConfigurationV1,
    runId: string,
): JsonObject {
    if (baseConfiguration.modelRouting !== undefined) {
        fail('$runRequest.configurationSnapshot.modelRouting', 'already present');
    }
    const clonedRouting = cloneCanonicalJson(modelRouting, '$runRequest.modelRouting');
    assertPortableModelRoutingConfigurationV1(clonedRouting, runId);
    return deepFreeze({
        ...cloneCanonicalJson(baseConfiguration, '$runRequest.configurationSnapshot'),
        modelRouting: clonedRouting,
    }) as unknown as JsonObject;
}
